import {
	createBrowserRouter,
	createMemoryRouter,
	RouterProvider,
} from 'react-router-dom';
import { useQuery } from 'react-query';
import { staticIdentityRoutes } from '@ashn/identity-react/Identity';
import { useMemo } from 'react';
import getEnvironment from './utils/getEnvironment';
import Layout from './components/shared/Layout';
import {
	useRoutesList,
	unauthorizedCallback,
	postLoginUnauthorizedCallback,
} from './hooks/useRoutesList';
import { getIsAuthenticated } from './data/apiCalls';

// Check for test environment
const isTestEnvironment =
	process.env.NODE_ENV === 'test' || typeof jest !== 'undefined';

const create = isTestEnvironment ? createMemoryRouter : createBrowserRouter;

const createRouter = (routes, isAuthenticated) =>
	create([
		// Identity route (no children unless you have identity-specific routes)
		{
			path: 'identity',
			id: 'identity',
			loader: async () => {
				const env = getEnvironment();
				let redirectUri = `https://${env}.ashlink.com/auth/callback`;
				if (env === 'prod') {
					redirectUri = 'https://www.ashlink.com/auth/callback';
				}
				return {
					apiEnvironment: env,
					clientId: 'ashlink.code',
					contactURL:
						'https://ashlink.com/ASH/public/Providers/Contact/default.aspx',
					customGenericErrorMessage: `We're sorry, we are unable to confirm your eligibility. If you are a clinical sub-account user, the primary account must register before you can create your account. Please contact the primary account holder. If you feel this is incorrect, please contact us.<br/>For clinical Users:  844-645-2746<br/>For Fitness Users: 877-329-2746, option 4`,
					customerServicePhoneNumber: '844-645-2746',
					customerServicePhoneNumberFitness: '877-329-2746',
					customerServiceOptionForFitness: '4',
					eligibilityURL: null,
					enableSSO: false,
					hasOwnProvider: false,
					includeOfflineAccessScope: true,
					ineligibleRegistration: false,
					overrideMainTitleTo: 'Login',
					path: 7,
					redirectUri,
					termsURL: '/termsconditions',
					isAuthenticated: isAuthenticated?.isLoggedIn,
					unauthorizedCallback,
					postLoginUnauthorizedCallback,
				};
			},
			element: <Layout />,
			children: [...staticIdentityRoutes],
			// Add identity-specific children here if needed
		},
		// Main app routes
		...routes,
		// ...staticIdentityRoutes,
	]);

function App() {
	const getIsAuthenticatedResponse = useQuery(
		['isAuthenticated'],
		() => getIsAuthenticated(),
		{
			staleTime: Infinity,
		}
	);

	const isAuthenticated = useMemo(
		() => ({
			isLoading: getIsAuthenticatedResponse.isLoading,
			isLoggedIn: getIsAuthenticatedResponse?.data?.status === 200,
		}),
		[
			getIsAuthenticatedResponse?.isLoading,
			getIsAuthenticatedResponse?.data,
			getIsAuthenticatedResponse?.error,
		]
	);

	const routes = useRoutesList();

	const router = useMemo(
		() => createRouter(routes, isAuthenticated),
		[routes, isAuthenticated?.isLoggedIn]
	);

	return <RouterProvider router={router} />;
}

export default App;
